import { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from '@tanstack/react-router';
import type { OrgRole, TrashView } from '@ddp/shared';
import Button from '@carbon/react/es/components/Button/Button';
import Loading from '@carbon/react/es/components/Loading/Loading';
import { InlineNotification } from '@carbon/react/es/components/Notification/Notification';

import { ApiError, apiFetch } from '../api/client.js';
import { useAuthStore } from '../auth/store.js';
import { PageHeader } from '../components/PageHeader.js';

const ROLE_RANK: Record<OrgRole, number> = {
  VIEWER: 0,
  ANNOTATOR: 1,
  EDITOR: 2,
  ADMIN: 3,
  OWNER: 4,
};

/**
 * Corbeille : vidéos et images supprimées, restaurables jusqu'à leur purge. La purge définitive
 * (irréversible) est réservée aux administrateurs.
 */
export function CorbeillePage() {
  const role = useAuthStore((state) => state.currentUser?.memberships[0]?.role ?? null);
  const canPurge = role !== null && ROLE_RANK[role] >= ROLE_RANK.ADMIN;

  const [view, setView] = useState<TrashView | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'forbidden' | 'error'>('loading');
  const [actionError, setActionError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const data = await apiFetch<TrashView>('/trash');
      setView(data);
      setStatus('ready');
    } catch (err) {
      if (err instanceof ApiError && (err.status === 403 || err.status === 404)) {
        setStatus('forbidden');
      } else {
        setStatus('error');
      }
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const act = async (path: string, method: 'POST' | 'DELETE') => {
    setActionError(null);
    try {
      await apiFetch(path, { method });
      await refresh();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Action impossible');
    }
  };

  if (status === 'loading') {
    return (
      <section className="ddp-page__section">
        <Loading withOverlay={false} description="Chargement…" />
      </section>
    );
  }

  if (status === 'forbidden') {
    return (
      <section className="ddp-page__section">
        <h1 className="ddp-trash__title">Corbeille</h1>
        <p className="ddp-videos__empty">Accès réservé aux éditeurs et administrateurs.</p>
        <RouterLink to="/data">← Vidéos</RouterLink>
      </section>
    );
  }

  if (status === 'error' || !view) {
    return (
      <section className="ddp-page__section">
        <InlineNotification
          kind="error"
          title="Erreur"
          subtitle="Chargement de la corbeille impossible."
          lowContrast
          hideCloseButton
        />
      </section>
    );
  }

  const empty = view.videos.length === 0 && view.images.length === 0;

  return (
    <section className="ddp-page__section ddp-trash">
      <RouterLink to="/data" className="ddp-trash__back">
        ← Vidéos
      </RouterLink>
      <PageHeader
        title="Corbeille"
        lead={`Les éléments supprimés restent restaurables ${view.retentionDays} jours, puis sont purgés définitivement.`}
      />

      {actionError && (
        <InlineNotification
          kind="error"
          title="Action impossible"
          subtitle={actionError}
          lowContrast
          hideCloseButton
        />
      )}

      {canPurge && !empty && (
        <div className="ddp-trash__toolbar">
          <Button
            kind="danger--tertiary"
            size="sm"
            onClick={() => void act('/trash/empty-expired', 'POST')}
          >
            Vider les éléments expirés
          </Button>
        </div>
      )}

      {empty && <p className="ddp-videos__empty">La corbeille est vide.</p>}

      {view.videos.length > 0 && (
        <>
          <h2 className="ddp-trash__subtitle">Vidéos ({view.videos.length})</h2>
          <ul className="ddp-trash__list">
            {view.videos.map((v) => (
              <li key={v.id} className="ddp-trash__item">
                <span className="ddp-trash__item-main">
                  <span className="ddp-trash__item-title" title={v.title}>
                    {v.title}
                  </span>
                  <span className="ddp-trash__item-meta">
                    Supprimée le {formatDate(v.deletedAt)} · purge le {formatDate(v.purgeAt)}
                  </span>
                </span>
                <span className="ddp-trash__item-actions">
                  <Button
                    kind="ghost"
                    size="sm"
                    onClick={() => void act(`/trash/videos/${v.id}/restore`, 'POST')}
                  >
                    Restaurer
                  </Button>
                  {canPurge && (
                    <Button
                      kind="danger--ghost"
                      size="sm"
                      onClick={() => void act(`/trash/videos/${v.id}`, 'DELETE')}
                    >
                      Supprimer
                    </Button>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}

      {view.images.length > 0 && (
        <>
          <h2 className="ddp-trash__subtitle">Images ({view.images.length})</h2>
          <ul className="ddp-trash__list">
            {view.images.map((img) => (
              <li key={img.id} className="ddp-trash__item">
                <img src={img.url} alt="" className="ddp-trash__thumb" />
                <span className="ddp-trash__item-main">
                  <span className="ddp-trash__item-title" title={img.videoTitle}>
                    {img.videoTitle}
                  </span>
                  <span className="ddp-trash__item-meta">
                    Image · purge le {formatDate(img.purgeAt)}
                  </span>
                </span>
                <span className="ddp-trash__item-actions">
                  <Button
                    kind="ghost"
                    size="sm"
                    onClick={() => void act(`/trash/images/${img.id}/restore`, 'POST')}
                  >
                    Restaurer
                  </Button>
                  {canPurge && (
                    <Button
                      kind="danger--ghost"
                      size="sm"
                      onClick={() => void act(`/trash/images/${img.id}`, 'DELETE')}
                    >
                      Supprimer
                    </Button>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

function formatDate(value: Date): string {
  return new Date(value).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
}
